import React, { useState } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { generateCurrencySymbol } from "app/routes/BuildProgram/Pricing/utils";
import OfferingActionModal from "../../../OfferingActionModal";
import { schedulingFetchDelete } from "app/utils/request/scheduling/fetch";
import { toast } from "react-toastify";
import RemainingSessions from "../RemainingSessions";
import { weekdayRestrictionLabel } from "../../../NewOffering/utils";
import { DateTime } from "luxon";
import { formatMonthDayWithYearIfNeeded } from "app/utils/luxonFormats";

interface RateLink {
  title: string;
  quantity: number;
  purchaseId: string;
  remainingSessions: number;
  id: number;
  unlimited: boolean;
  // CN-2832: the part of remainingSessions carried over from earlier cycles.
  carriedOverSessions?: number;
}

interface Subscription {
  fullName?: string;
  profilePicture: string;
  offeringTitle: string;
  billingFrequency: number | null;
  nextInvoiceDate: string | null;
  // CN-2832: signed up before a custom start date; the first charge is on it.
  billingStartsAt?: string | null;
  // CN-2641: end date of a capped subscription run (null when uncapped).
  subscriptionEndsAt?: string | null;
  expiredAt?: string | null;
  productPrice: number;
  currency: string;
  remainingSessions: number | null;
  unlimited?: boolean;
  productType?: string;
  productId?: string;
  coachId?: string;
  studentProfileId?: string;
  rateLinks?: RateLink[];
  cancelled?: boolean;
  restrictWeekdays?: boolean;
  restrictWeekends?: boolean;
  weekdayBeginAt?: number;
  weekdayEndAt?: number;
}

interface SubscriptionCardWithRatesProps {
  subscription: Subscription;
  formatInvoiceDate: (isoDate: string) => string;
  handleDelete: (productId: string) => void;
  updateRemainingSessions?: (
    purchaseId: string,
    incrementValue: number
  ) => void;
  isCoach?: boolean;
  purchaseId?: string;
}

export default function SubscriptionCardWithRates({
  subscription,
  formatInvoiceDate,
  handleDelete,
  updateRemainingSessions,
  isCoach = false,
  purchaseId
}: SubscriptionCardWithRatesProps) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const handleDeleteClick = () => setShowModal(true);
  const handleCancel = () => setShowModal(false);

  const handleConfirm = () => {
    const productData = {
      coach_id: subscription.coachId,
      stripe_product_id: subscription.productId,
      student_profile_id: subscription.studentProfileId,
    };

    schedulingFetchDelete(`/stripe_purchase`, productData)
      .then(() => {
        toast.success(t("Subscription canceled"));
        setShowModal(false);
        handleDelete(subscription.productId || "");
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
        setShowModal(false);
      });
  };

  const formattedDate = subscription?.nextInvoiceDate
    ? formatInvoiceDate(subscription?.nextInvoiceDate)
    : t("N/A");

  // CN-2832: a subscription waiting for its start date was never valid, so
  // "remain valid until" would be wrong; say what cancelling actually stops.
  const startsOn = subscription?.billingStartsAt ? formatInvoiceDate(subscription.billingStartsAt) : null;
  const scheduledMessage = isCoach
    ? "Are you sure you want to cancel the athlete's \"{{title}}\" subscription? It hasn't started yet, so they won't be charged on {{date}} or after."
    : "Are you sure you want to cancel your \"{{title}}\" subscription? It hasn't started yet, so you won't be charged on {{date}} or after.";
  const activeMessage = isCoach
    ? "Are you sure you want to cancel the athlete's \"{{title}}\" subscription? If you cancel, they won't be charged at the next billing cycle. It will remain valid until {{date}}."
    : "Are you sure you want to cancel your \"{{title}}\" subscription? If you cancel, you won't be charged at the next billing cycle. It will remain valid until {{date}}.";

  const modalContent = (
    <div>
      {t(startsOn ? scheduledMessage : activeMessage, {
        title: subscription?.offeringTitle,
        date: startsOn || formattedDate,
      })}
    </div>
  );

  return (
    <div className={styles.subscriptionCardCoach}>
      <div className={styles.topSection}>
        <div className={styles.iconWrapper}>
          <i className="fas fa-sync" />
        </div>
        <div className={styles.infoBlock}>
          <p className={styles.subscriptionName}>{subscription.offeringTitle}</p>
          <div className={styles.subscriptionRow}>
            {subscription.billingStartsAt ? (
              <p className={styles.subscriptionRenewal}>
                {t("Scheduled – first charge on {{date}} for {{currency}}{{amount}}", {
                  date: formatInvoiceDate(subscription.billingStartsAt),
                  currency: generateCurrencySymbol(subscription.currency),
                  amount: subscription.productPrice,
                })}
              </p>
            ) : subscription.nextInvoiceDate ? (
              <>
                {subscription.billingFrequency != null && (
                  <>
                    <p className={styles.subscriptionDetails}>
                      {t("Billing {{billingFrequency}}", {
                        billingFrequency:
                          Number(subscription.billingFrequency) === 1
                            ? t("monthly")
                            : `${subscription.billingFrequency} ${t("months")}`,
                      })}
                    </p>
                    <span className={styles.dotSeparator}>•</span>
                  </>
                )}
                <p className={styles.subscriptionRenewal}>
                  {t("Auto renewal: next invoice on {{date}} for {{currency}}{{amount}}", {
                    date: formattedDate,
                    currency: generateCurrencySymbol(subscription.currency),
                    amount: subscription.productPrice,
                  })}
                </p>
                {/* CN-2641: a capped subscription has a known last day — same
                    line the rate-link-less SubscriptionCard shows. */}
                {subscription.subscriptionEndsAt && (
                  <>
                    <span className={styles.dotSeparator}>•</span>
                    <p className={styles.subscriptionRenewal}>
                      {t("Ends on {{date}}", {
                        date: formatMonthDayWithYearIfNeeded(
                          DateTime.fromISO(subscription.subscriptionEndsAt),
                        ),
                      })}
                    </p>
                  </>
                )}
              </>
            ) : (
              <p className={styles.subscriptionRenewal}>
                {subscription.expiredAt
                  ? t("Cancelled - expires on {{date}}", {
                      date: formatInvoiceDate(subscription.expiredAt),
                    })
                  : t("Cancelled")}
              </p>
            )}
          </div>
        </div>
        {subscription.nextInvoiceDate && !subscription.cancelled && (
          <button className={styles.deleteButton} onClick={handleDeleteClick}>
            <i className="fas fa-times" />
          </button>
        )}
      </div>

      {subscription.restrictWeekdays && !subscription.restrictWeekends && (
        <div className={styles.restriction_area}>
          <span className={styles.restriction_badge}>
            {weekdayRestrictionLabel(
              subscription.weekdayBeginAt ?? 0,
              subscription.weekdayEndAt ?? 1440,
              t("Weekdays only"),
            )}
          </span>
        </div>
      )}
      {subscription.restrictWeekends && !subscription.restrictWeekdays && (
        <div className={styles.restriction_area}>
          <span className={styles.restriction_badge}>
            {t("Weekends & Holidays only")}
          </span>
        </div>
      )}

      {isCoach && subscription.rateLinks?.length > 0 && (
        <>
          <div className={styles.dividerLine} />
          <div className={styles.rate_links_container_coach}>
            {subscription.rateLinks.map((link, idx) => (
              <div className={styles.rate_link_row_coach} key={idx}>
                <div className={styles.rate_link_left_coach}>
                  <span className={styles.rate_link_title_coach}>{link.title}</span>
                  <p className={styles.rate_link_description}>
                    {t("Here you can set amount of remaining sessions for this athlete")}
                  </p>
                  {/* CN-2832: same count the athlete sees on their card. */}
                  {(link.carriedOverSessions ?? 0) > 0 && (
                    <p className={styles.rate_link_description}>
                      {t("{{count}} carried over from earlier cycles", { count: link.carriedOverSessions })}
                    </p>
                  )}
                </div>
                <RemainingSessions
                  remaining={link.remainingSessions}
                  unlimited={link.unlimited}
                  updateRemainingSessions={updateRemainingSessions}
                  purchaseId={purchaseId}
                  rateLinkId={link.id}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {showModal && (
        <OfferingActionModal
          actions={{ onCancel: handleCancel, onConfirm: handleConfirm }}
          data={{
            title: t("Are you sure you want to cancel subscription?"),
            content: modalContent,
            button_text: t("Yes, cancel it"),
            operation: "delete",
          }}
          cancelButton={t("No, keep it")}
        />
      )}
    </div>
  );
}
